<template>
  <div v-if="showFilters" class="filters-container">
    <!-- Service Filter (1st) -->
    <div v-if="entityConfig.requiresServiceFilter || entityConfig.customFilters?.service?.showAlways" class="filter-section">
      <label for="serviceFilter">{{ entityConfig.customFilters?.service?.label || 'Filter by Service' }}:</label>
      <select id="serviceFilter" v-model="selectedService" @change="$emit('serviceChange', selectedService)">
        <option value="">-- Select a Service --</option>
        <option v-for="service in serviceOptions" :key="service.value" :value="service.value">
          {{ service.label }}
        </option>
      </select>
    </div>
    
    <!-- PSCU Client Filter (2nd) -->
    <div v-if="entityConfig.customFilters?.pscuClient?.showAlways" class="filter-section">
      <label for="pscuClientFilter">{{ entityConfig.customFilters.pscuClient.label }}:</label>
      <select 
        id="pscuClientFilter" 
        v-model="selectedPscuClient" 
        :key="`pscu-${environmentKey}`"
        @change="$emit('pscuClientChange', selectedPscuClient)"
      >
        <option value="">-- Select a PSCU Client --</option>
        <option v-for="client in pscuClientOptions" :key="client.value" :value="client.value">
          {{ client.label }}
        </option>
      </select>
    </div>
    
    <!-- Product Filter (3rd) -->
    <div v-if="entityConfig.requiresProductFilter" class="filter-section">
      <label for="productFilter">Filter by Product:</label>
      <select 
        id="productFilter" 
        v-model="selectedProduct" 
        :key="`product-${environmentKey}`"
        @change="$emit('productChange', selectedProduct)"
      >
        <option value="">-- Select a Product --</option>
        <option v-for="product in filteredProductOptions" :key="product.value" :value="product.value">
          {{ product.label }}
        </option>
      </select>
    </div>
    
    <!-- Source Service Filter (4th) -->
    <div v-if="entityConfig.customFilters?.sourceService?.showAlways" class="filter-section">
      <label for="sourceServiceFilter">Filter by Source Service:</label>
      <select 
        id="sourceServiceFilter" 
        v-model="selectedSourceService" 
        :key="`source-${environmentKey}`"
        @change="$emit('sourceServiceChange', selectedSourceService)"
      >
        <option value="">-- Select a Source Service --</option>
        <option v-for="service in serviceOptions" :key="service.value" :value="service.value">
          {{ service.label }}
        </option>
      </select>
    </div>
    
    <!-- Target Service Filter (5th) -->
    <div v-if="entityConfig.requiresTargetServiceFilter || entityConfig.customFilters?.targetService?.showAlways" class="filter-section">
      <label for="targetServiceFilter">Filter by Target Service:</label>
      <select 
        id="targetServiceFilter" 
        v-model="selectedTargetService" 
        :key="`target-${environmentKey}`"
        @change="$emit('targetServiceChange', selectedTargetService)"
      >
        <option value="">-- Select a Target Service --</option>
        <option v-for="service in serviceOptions" :key="service.value" :value="service.value">
          {{ service.label }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed } from 'vue';

const props = defineProps({
  entityName: { type: String, required: true },
  showFilters: { type: Boolean, default: true },
  serviceOptions: { type: Array, default: () => [] },
  productOptions: { type: Array, default: () => [] },
  pscuClientOptions: { type: Array, default: () => [] },
  serviceFilter: { type: String, default: '' },
  productFilter: { type: String, default: '' },
  pscuClientFilter: { type: String, default: '' },
  targetServiceFilter: { type: String, default: '' },
  sourceServiceFilter: { type: String, default: '' },
  entityConfig: { type: Object, default: () => ({}) }
});

const emit = defineEmits(['serviceChange', 'productChange', 'pscuClientChange', 'targetServiceChange', 'sourceServiceChange']);

const selectedService = ref(props.serviceFilter);
const selectedProduct = ref(props.productFilter);
const selectedPscuClient = ref(props.pscuClientFilter);
const selectedTargetService = ref(props.targetServiceFilter);
const selectedSourceService = ref(props.sourceServiceFilter);

// Environment-based key for forcing re-renders
const environmentKey = ref(localStorage.getItem('selectedEnvironment') || 'dev');

// Filter product options based on selected PSCU Client
const filteredProductOptions = computed(() => {
  if (!selectedPscuClient.value) {
    return props.productOptions;
  }
  
  const filtered = props.productOptions.filter(product => {
    return product.pscuClientId == selectedPscuClient.value;
  });
  
  return filtered;
});

// Watch for external changes
watch(() => props.serviceFilter, (newVal) => {
  selectedService.value = newVal;
});

watch(() => props.productFilter, (newVal) => {
  selectedProduct.value = newVal;
});

watch(() => props.pscuClientFilter, (newVal) => {
  selectedPscuClient.value = newVal;
});

// Clear product selection when PSCU Client changes
watch(selectedPscuClient, (newVal, oldVal) => {
  if (newVal !== oldVal && selectedProduct.value) {
    // Check if current product is still valid for new PSCU Client
    const isProductValid = filteredProductOptions.value.some(p => p.value === selectedProduct.value);
    if (!isProductValid) {
      selectedProduct.value = '';
      emit('productChange', '');
    }
  }
});

watch(() => props.targetServiceFilter, (newVal) => {
  selectedTargetService.value = newVal;
});

watch(() => props.sourceServiceFilter, (newVal) => {
  selectedSourceService.value = newVal;
});

// Watch for environment changes and reset all filters
watch(() => localStorage.getItem('selectedEnvironment'), (newEnv, oldEnv) => {
  if (newEnv !== oldEnv && oldEnv !== null) {
    // Update environment key to force re-render
    environmentKey.value = newEnv;
    
    selectedService.value = '';
    selectedProduct.value = '';
    selectedPscuClient.value = '';
    selectedTargetService.value = '';
    selectedSourceService.value = '';
    
    // Emit change events to notify parent components
    emit('serviceChange', '');
    emit('productChange', '');
    emit('pscuClientChange', '');
    emit('targetServiceChange', '');
    emit('sourceServiceChange', '');
  }
}, { immediate: false });

// Listen for custom environment change events
window.addEventListener('environmentChanged', (event) => {
  const newEnv = event.detail.environment;
  
  // Update environment key to force re-render
  environmentKey.value = newEnv;
  
  selectedService.value = '';
  selectedProduct.value = '';
  selectedPscuClient.value = '';
  selectedTargetService.value = '';
  selectedSourceService.value = '';
  
  // Emit change events to notify parent components
  emit('serviceChange', '');
  emit('productChange', '');
  emit('pscuClientChange', '');
  emit('targetServiceChange', '');
  emit('sourceServiceChange', '');
});
</script>

<style scoped>
.filters-container {
  margin-bottom: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: flex-end;
  padding: 12px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--bg-color);
}

.filter-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-section label {
  font-weight: bold;
  white-space: nowrap;
}

.filter-section select {
  padding: 8px;
  min-width: 250px;
  border: 1px solid var(--border-color);
  border-radius: 4px;
  background: var(--input-bg);
  color: var(--text-color);
}
</style>